import type { AuthError } from "@supabase/supabase-js";

import { PASSWORD_MIN_LENGTH } from "./schemas";

/** Supabase OAuth code-exchange error whose root cause is context isolation:
 *  the PKCE verifier cookie (and usually the whole cookie jar) is absent at the
 *  callback because sign-in started in an isolated browser context (in-app
 *  browser / standalone PWA / fresh partition) — see #685. Exported as the
 *  single source of truth so the login page branches on the same code it's
 *  mapped to here. */
export const PKCE_VERIFIER_NOT_FOUND_CODE = "pkce_code_verifier_not_found";

/** The #685 context-isolation error CLASS: the PKCE verifier in the callback
 *  context's cookie jar is missing or mismatched because the flow's start and
 *  callback ran in different browser contexts. Single source of truth consumed
 *  by (1) the friendly-copy map below, (2) the /login recovery card
 *  (`_oauth-recovery.tsx`), and (3) the callback's one-shot auto-retry
 *  (`oauth-retry.ts`) — add a sibling code HERE and all three stay in
 *  lockstep. */
export const CONTEXT_ISOLATION_ERROR_CODES: ReadonlySet<string> = new Set([
  PKCE_VERIFIER_NOT_FOUND_CODE,
  // A verifier WAS present but doesn't match the code — the cross-context
  // sibling shape (two starts in different contexts; the callback landed
  // where a stale verifier lives). Same clean-restart recovery applies.
  "bad_code_verifier",
]);

// One copy string for the whole isolation class — deliberately identical
// across codes: the prod failure-rate query matches this copy in the URL
// (`$current_url ILIKE '%Sign-in+didn%27t+finish%'`, see the #685 memory
// note), so per-code variants would silently drop out of monitoring.
const CONTEXT_ISOLATION_MESSAGE =
  "Sign-in didn't finish. Please try again, and if it keeps happening, use your email and password.";

/** Copy for Supabase's server-side `weak_password` rejection, split by the
 *  reason GoTrue returns in `AuthWeakPasswordError.reasons` (one of
 *  "length" | "characters" | "pwned"). The client Zod (`signupSchema`) already
 *  blocks length/character failures BEFORE submit, so the reason that actually
 *  reaches here is almost always `pwned`: a password that satisfies every
 *  complexity rule but appears in the HaveIBeenPwned breach corpus (enabled via
 *  the dashboard-only `password_hibp_enabled` setting). Until #895-followup this
 *  branch was collapsed into the complexity line, so a breached-but-complex
 *  password told the user to add the "upper, lower, and a digit" it already had
 *  — an unsolvable loop (nursing creator, 2026-07-20; prod-verified reasons:
 *  ["pwned"]). Keep both messages honest to the reason. */
const WEAK_PASSWORD_COMPLEXITY = `Password must be at least ${PASSWORD_MIN_LENGTH} characters with upper, lower, and a digit.`;
const WEAK_PASSWORD_PWNED =
  "This password showed up in a known public data breach, so it can't be used, even though it meets the length and character rules. That means this exact password sits on public lists that attackers try first; it does NOT mean your account was compromised. Please choose a different, unique password (a random passphrase or your password manager's generator is ideal).";

// Source: https://supabase.com/docs/reference/javascript/v2/auth-error-codes
// NOTE: `weak_password` is handled specially in `authErrorMessage` (reason-aware),
// so it is intentionally NOT in this static map.
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  invalid_credentials: "Email or password is incorrect.",
  email_address_invalid: "That doesn't look like a valid email.",
  user_already_exists: "An account with that email already exists. Try signing in.",
  email_exists: "An account with that email already exists. Try signing in.",
  over_email_send_rate_limit: "Too many requests. Try again in a minute.",
  email_not_confirmed: "Please confirm your email before signing in.",
  same_password: "New password must be different from the old one.",
  signup_disabled: "New signups are temporarily disabled.",
  // The #685 context-isolation class (see CONTEXT_ISOLATION_ERROR_CODES
  // above). Don't surface auth-js's raw strings; the login page pairs this
  // copy with actionable recovery guidance.
  [PKCE_VERIFIER_NOT_FOUND_CODE]: CONTEXT_ISOLATION_MESSAGE,
  bad_code_verifier: CONTEXT_ISOLATION_MESSAGE,
  over_request_rate_limit: "Too many requests. Try again in a minute.",
  validation_failed: "Please check your input and try again.",
};

const FALLBACK = "Something went wrong. Please try again.";

// Codes whose copy is about the password value the user just typed (vs.
// session/network issues). Surface inline on the password field instead
// of as a top-level form banner.
const PASSWORD_FIELD_ERROR_CODES = new Set(["weak_password", "same_password"]);

type AuthReply =
  | { fieldErrors: { password: string[] } }
  | { formErrors: string[] };

/** The subset of a Supabase `AuthError` this module reads. `reasons` is present
 *  only on `AuthWeakPasswordError` (a subclass), so it's optional here — plain
 *  `AuthError`s still satisfy the shape. Typed as `readonly string[]` rather than
 *  importing `WeakPasswordReasons` to avoid coupling to the auth-js export path;
 *  we only ever `.includes("pwned")`. */
type AuthErrorLike = Pick<AuthError, "code" | "message"> & {
  reasons?: readonly string[];
};

/** Friendly copy for a Supabase AuthError. Used by the OAuth flow's
 *  `?oauth_error=` redirect (string) and as the input to `authErrorReply`
 *  (which wraps it in Conform's reply shape). */
export function authErrorMessage(
  error: AuthErrorLike | null | undefined,
): string {
  if (!error) return FALLBACK;

  // OAuth provider not configured in the Supabase dashboard — Supabase
  // returns this with code=validation_failed + a "provider is not enabled"
  // message, which our generic validation_failed copy ("check your input")
  // would mis-route. Pattern-match on the message instead.
  if (error.message && /provider is not enabled/i.test(error.message)) {
    return "Sign-in with that provider is temporarily unavailable. Try email and password.";
  }

  // `weak_password` is reason-aware: HIBP breach matches (`pwned`) need honest
  // copy, NOT the complexity line — the password already met complexity or the
  // client Zod would have blocked it. See WEAK_PASSWORD_* above.
  if (error.code === "weak_password") {
    return error.reasons?.includes("pwned")
      ? WEAK_PASSWORD_PWNED
      : WEAK_PASSWORD_COMPLEXITY;
  }

  if (error.code) {
    const mapped = AUTH_ERROR_MESSAGES[error.code];
    if (mapped) return mapped;
  }
  return error.message || FALLBACK;
}

/** Map a Supabase AuthError to the shape `submission.reply()` expects.
 *  Password-shaped codes route to the field; everything else to the
 *  form banner. */
export function authErrorReply(
  error: AuthErrorLike | null | undefined,
): AuthReply {
  const message = authErrorMessage(error);
  if (error?.code && PASSWORD_FIELD_ERROR_CODES.has(error.code)) {
    return { fieldErrors: { password: [message] } };
  }
  return { formErrors: [message] };
}
